import { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import TablePagination from "../common/TablePagination";
import { Check, X, FileText } from "lucide-react";
import {
  getPendingApprovals,
  approveUserRequest,
  rejectUserRequest,
} from "../../services/societyService";
import {
  normalizePropertyType,
  PROPERTY_CATEGORIES,
} from "../../utils/propertyStructure";

const PropertyApprovals = ({ societyId, propertyType, onRequestsUpdated }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = normalizedCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const [activeTab, setActiveTab] = useState("users"); // "users" or "secondary"
  const [userFilter, setUserFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (societyId) fetchRequests();
  }, [societyId]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await getPendingApprovals(societyId);
      const list = Array.isArray(data) ? data : data?.data || [];
      setRequests(list);
      if (typeof onRequestsUpdated === "function") {
        onRequestsUpdated(list.length);
      }
    } catch (err) {
      console.error(err);
      setRequests([]);
      if (typeof onRequestsUpdated === "function") {
        onRequestsUpdated(0);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await approveUserRequest(societyId, selectedRequest.id);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await rejectUserRequest(societyId, selectedRequest.id, rejectReason);
      setSelectedRequest(null);
      setIsRejecting(false);
      setRejectReason("");
      fetchRequests();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const filters = isIndustrial
    ? [
      { key: "all", label: "All Requests" },
      { key: "executive", label: "Executives & HODs" },
      { key: "employee", label: "Staff & Employees" },
      { key: "contractor", label: "Contractors & Vendors" },
    ]
    : isCommercial
      ? [
        { key: "all", label: "All Requests" },
        { key: "owner", label: "Store Owners" },
        { key: "tenant", label: "Store Staff" },
        { key: "contractor", label: "Contractors" },
      ]
      : [
        { key: "all", label: "All" },
        { key: "owner", label: "Owner" },
        { key: "tenant", label: "Tenant" },
        { key: "family", label: "Family" },
      ];

  const filteredUsers = Array.isArray(requests)
    ? requests.filter((user) => {
      if (userFilter === "all") return true;
      const type = (user.type || user.role || "").toLowerCase();
      if (userFilter === "executive") {
        return type.includes("lead") || type.includes("hod") || type.includes("director") || type.includes("manager");
      }
      if (userFilter === "employee") {
        return !type.includes("lead") && !type.includes("hod") && !type.includes("contractor");
      }
      return type.includes(userFilter);
    })
    : [];

  const paginatedUsers = filteredUsers.slice((page - 1) * limit, page * limit);

  return (
    <div
      style={{
        padding: "24px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h3
          style={{
            color: "#0f172a",
            fontSize: "18px",
            fontWeight: "700",
            margin: 0,
            fontFamily: "var(--font-heading, Montserrat, sans-serif)",
          }}
        >
          {isIndustrial
            ? "Staff & Workplace Access Approvals"
            : isCommercial
              ? "Tenant & Commercial Access Approvals"
              : "Approvals & Verification Requests"}
        </h3>
      </div>

      {/* Main Tabs */}
      <div
        style={{
          display: "flex",
          gap: "24px",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: "24px",
        }}
      >
        <button
          onClick={() => { setActiveTab("users"); setPage(1); }}
          style={{
            background: "none",
            border: "none",
            padding: "10px 4px",
            fontSize: "15px",
            fontWeight: "600",
            cursor: "pointer",
            color: activeTab === "users" ? "var(--color-dark-bg, #27306f)" : "#64748b",
            borderBottom:
              activeTab === "users"
                ? "2px solid var(--color-dark-bg, #27306f)"
                : "2px solid transparent",
          }}
        >
          {isIndustrial
            ? `Staff Requests (${Array.isArray(requests) ? requests.length : 0})`
            : isCommercial
              ? `Tenant Requests (${Array.isArray(requests) ? requests.length : 0})`
              : `User Requests (${Array.isArray(requests) ? requests.length : 0})`}
        </button>
        <button
          onClick={() => setActiveTab("secondary")}
          style={{
            background: "none",
            border: "none",
            padding: "10px 4px",
            fontSize: "15px",
            fontWeight: "600",
            cursor: "pointer",
            color: activeTab === "secondary" ? "var(--color-dark-bg, #27306f)" : "#64748b",
            borderBottom:
              activeTab === "secondary"
                ? "2px solid var(--color-dark-bg, #27306f)"
                : "2px solid transparent",
          }}
        >
          {isIndustrial
            ? "Material & Bay Pass Approvals (0)"
            : isCommercial
              ? "Signage & Lease Approvals (0)"
              : "Community Post Requests (0)"}
        </button>
      </div>

      {/* User Requests View */}
      {activeTab === "users" && (
        <div>
          {/* Sub-filters for Users */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => { setUserFilter(f.key); setPage(1); }}
                style={{
                  padding: "6px 14px",
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  border: userFilter === f.key ? "1px solid var(--color-dark-bg, #27306f)" : "1px solid #e2e8f0",
                  background: userFilter === f.key ? "var(--color-dark-bg, #27306f)" : "#fff",
                  color: userFilter === f.key ? "#ffffff" : "#64748b",
                  transition: "all 150ms ease",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* User List */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
            {loading ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                Loading verification requests...
              </div>
            ) : filteredUsers.length > 0 ? (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                  <thead>
                    <tr
                      style={{
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        color: "#475569",
                        fontSize: "13px",
                      }}
                    >
                      <th style={{ padding: "12px 16px", fontWeight: "600" }}>Name</th>
                      <th style={{ padding: "12px 16px", fontWeight: "600" }}>
                        {isIndustrial ? "Desk / Cabin / Dept" : isCommercial ? "Store / Office" : "Unit"}
                      </th>
                      <th style={{ padding: "12px 16px", fontWeight: "600" }}>
                        {isIndustrial ? "Designation / Role" : "Role"}
                      </th>
                      <th style={{ padding: "12px 16px", fontWeight: "600" }}>Date Applied</th>
                      <th style={{ padding: "12px 16px", fontWeight: "600", textAlign: "right" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => (
                      <tr key={user.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                        <td style={{ padding: "12px 16px", color: "#0f172a", fontWeight: "600" }}>
                          {user.name}
                        </td>
                        <td style={{ padding: "12px 16px", color: "#475569" }}>
                          {user.unit || "N/A"}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span
                            style={{
                              background:
                                user.type === "owner" || String(user.type).includes("lead")
                                  ? "#dcfce7"
                                  : "#f1f5f9",
                              color:
                                user.type === "owner" || String(user.type).includes("lead")
                                  ? "#166534"
                                  : "#475569",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "600",
                              textTransform: "capitalize",
                            }}
                          >
                            {user.type || "Staff"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", color: "#64748b" }}>
                          {user.date || "Just now"}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <button
                            onClick={() => {
                              setSelectedRequest(user);
                              setIsRejecting(false);
                              setRejectReason("");
                            }}
                            style={{
                              padding: "6px 14px",
                              background: "var(--color-dark-bg, #27306f)",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                            }}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination
                  totalItems={filteredUsers.length}
                  page={page}
                  limit={limit}
                  onPageChange={setPage}
                  onLimitChange={setLimit}
                />
              </>
            ) : (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                No pending requests match the selected filter.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Secondary Requests View */}
      {activeTab === "secondary" && (
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "40px",
            textAlign: "center",
            color: "#64748b",
          }}
        >
          {isIndustrial
            ? "No pending material dispatch or bay gate pass clearance requests."
            : isCommercial
              ? "No pending commercial signage or fit-out requests."
              : "No pending community post requests."}
        </div>
      )}

      {/* Review Request Modal */}
      {selectedRequest && (
        <Modal
          isOpen={true}
          onClose={() => {
            setSelectedRequest(null);
            setIsRejecting(false);
            setRejectReason("");
          }}
          title={`Review Request: ${selectedRequest.name}`}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div
              style={{
                background: "#f8fafc",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>
                  {isIndustrial
                    ? "Desk / Cabin Requested:"
                    : isCommercial
                      ? "Commercial Space Requested:"
                      : "Unit Requested:"}
                </strong>{" "}
                {selectedRequest.unit || "N/A"}
              </p>
              <p style={{ margin: "0 0 8px 0" }}>
                <strong>Designation / Role:</strong>{" "}
                <span style={{ textTransform: "capitalize" }}>{selectedRequest.type}</span>
              </p>
              <p style={{ margin: 0 }}>
                <strong>Date Applied:</strong> {selectedRequest.date || "Just now"}
              </p>
            </div>

            <div>
              <h4 style={{ margin: "0 0 8px 0", color: "#0f172a" }}>Submitted Credentials & KYC</h4>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: "#f8fafc",
                }}
              >
                <FileText size={20} color="var(--color-dark-bg)" />
                <span style={{ color: "var(--color-dark-bg)", fontWeight: "600", textDecoration: "underline" }}>
                  View Verification Document.pdf
                </span>
              </div>
            </div>

            {isRejecting ? (
              <div style={{ marginTop: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#0f172a",
                  }}
                >
                  Reason for Rejection (Required)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. Unverified employee ID, invalid department clearance, etc."
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    minHeight: "80px",
                    marginBottom: "16px",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                  <button
                    disabled={actionLoading}
                    onClick={() => setIsRejecting(false)}
                    style={{
                      padding: "8px 16px",
                      background: "#fff",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={!rejectReason.trim() || actionLoading}
                    style={{
                      padding: "8px 16px",
                      background: "#ef4444",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: rejectReason.trim() && !actionLoading ? "pointer" : "not-allowed",
                      opacity: rejectReason.trim() && !actionLoading ? 1 : 0.5,
                      fontWeight: "600",
                    }}
                  >
                    {actionLoading ? "Rejecting..." : "Confirm Rejection"}
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  marginTop: "24px",
                }}
              >
                <button
                  disabled={actionLoading}
                  onClick={() => setIsRejecting(true)}
                  style={{
                    padding: "8px 16px",
                    background: "#fff",
                    border: "1px solid #ef4444",
                    color: "#ef4444",
                    borderRadius: "6px",
                    cursor: actionLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "600",
                    opacity: actionLoading ? 0.5 : 1,
                  }}
                >
                  <X size={16} /> Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  style={{
                    padding: "8px 16px",
                    background: "#22c55e",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: actionLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: "600",
                    opacity: actionLoading ? 0.5 : 1,
                  }}
                >
                  <Check size={16} /> {actionLoading ? "Approving..." : "Approve Clearance"}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PropertyApprovals;
